import { useEffect, useMemo, useState } from 'react'

import {
  assignWaveHoles,
  assignWaveSequentialTimes,
  autoGenerateNextDayFromWave,
  autoAssignShotgunHolesAllFlights,
  buildBracketWaveGroups,
  buildWaveGroups,
  pbccRotationFor,
  setGroupAssignment,
  setTeamStartingHole,
  setWaveAssignments,
  setWaveFlights,
  setWaveStartTime,
  waveAdvanceReadiness,
} from '../lib/data.js'
import { lastNames } from '../lib/teamName.js'

// Tournaments default to 1 day; admin can extend in setup.
function dayList(days) {
  const n = Math.max(1, Math.min(8, Number(days) || 1))
  return Array.from({ length: n }, (_, i) => i + 1)
}

export default function TeeTimesTab({ tournamentId, tournament, flights, tournamentInfo, teeTimes, brackets }) {
  const days = dayList(tournamentInfo?.tournamentDays)
  const [selectedDay, setSelectedDay] = useState(1)
  const startType = tournamentInfo?.startType || 'shotgun'
  const sortedFlights = useMemo(
    () => flights.slice().sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
    [flights],
  )

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">
            Tee Times — {startType === 'shotgun' ? 'Shotgun Start' : 'Sequential from Hole 1'}
          </h2>
          <p className="text-base text-gray-500">
            {startType === 'shotgun'
              ? 'Two waves per day — assign flights to a wave then auto-assign holes. Admin can override any group\'s starting hole.'
              : 'Set a start time and interval per day; the system generates group tee times in flight order.'}
          </p>
        </div>
        <div className="flex gap-1 items-center flex-wrap">
          {days.map((d) => (
            <button
              key={d}
              onClick={() => setSelectedDay(d)}
              className={`px-3 py-1.5 rounded-lg text-base font-bold ${
                d === selectedDay ? 'bg-green-deep text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}>
              Day {d}
            </button>
          ))}
        </div>
      </div>

      {sortedFlights.length === 0 ? (
        <div className="card text-center text-gray-500 py-8">
          No flights yet — add flights first, then return to set up tee times.
        </div>
      ) : tournamentInfo?.format === 'Scramble' && startType === 'shotgun' ? (
        // Scramble shotgun is one start, one team per hole — no waves, no
        // matches, no flighting. Use the simplified panel.
        <ScrambleShotgun
          tournamentId={tournamentId}
          day={selectedDay}
          flights={sortedFlights}
          dayData={teeTimes?.[selectedDay] || {}}
        />
      ) : startType === 'shotgun' ? (
        <ShotgunDay
          tournamentId={tournamentId}
          day={selectedDay}
          flights={sortedFlights}
          tournamentInfo={tournamentInfo}
          dayData={teeTimes?.[selectedDay] || {}}
          tournament={tournament}
          brackets={brackets || {}}
        />
      ) : (
        <SequentialDay
          tournamentId={tournamentId}
          day={selectedDay}
          flights={sortedFlights}
          tournamentInfo={tournamentInfo}
          dayData={teeTimes?.[selectedDay] || {}}
        />
      )}
    </div>
  )
}

// ── Scramble shotgun panel ────────────────────────────────────────────────
//
// Simple table: one row per team, columns Team Name + Starting Hole. No
// wave concept (a 4-man scramble runs as a single field). No "Match"
// column. Auto-Assign button calls the existing shotgun-spread helper;
// Start Time stores at teeTimes/{day}/waves/wave1/startTime so the rest
// of the app's read paths keep working unchanged.
function ScrambleShotgun({ tournamentId, day, flights, dayData }) {
  const [timeDraft, setTimeDraft] = useState(dayData?.waves?.wave1?.startTime || '8:00 AM')
  // Sync the start-time input when another admin saves a different value.
  useEffect(() => {
    setTimeDraft(dayData?.waves?.wave1?.startTime || '8:00 AM')
  }, [dayData?.waves?.wave1?.startTime])

  // Flatten teams across all flights (a Scramble usually has one flight
  // anyway). Sort by current startingHole, then by team-id for stable
  // order before any holes are assigned.
  const teams = useMemo(() => {
    const out = []
    for (const f of flights) {
      const teamMap = f.teams || {}
      for (const [tid, t] of Object.entries(teamMap)) {
        out.push({ flightId: f.id, teamId: tid, info: t.info || {} })
      }
    }
    out.sort((a, b) => {
      const ah = Number(a.info.startingHole) || 99
      const bh = Number(b.info.startingHole) || 99
      if (ah !== bh) return ah - bh
      return a.teamId < b.teamId ? -1 : 1
    })
    return out
  }, [flights])

  const saveStartTime = async () => {
    await setWaveStartTime(tournamentId, day, 'wave1', (timeDraft || '').trim() || '8:00 AM')
  }

  const autoAssign = async () => {
    const n = await autoAssignShotgunHolesAllFlights(tournamentId)
    console.log('[scramble shotgun] auto-assigned across', n, 'flight(s)')
  }

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="text-sm uppercase tracking-widest font-bold text-gray-500">Shotgun Start Time</label>
          <input
            className="input mt-1"
            value={timeDraft}
            onChange={(e) => setTimeDraft(e.target.value)}
            onBlur={saveStartTime}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() } }}
            placeholder="8:00 AM"
          />
        </div>
        <div className="shrink-0 mt-3 sm:mt-5">
          <button className="btn-primary" onClick={autoAssign}>
            ⚡ Auto-Assign Holes
          </button>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-base">Hole Assignments</h3>
          <p className="text-sm text-gray-500">
            {teams.length} team{teams.length === 1 ? '' : 's'}
            {teams.length > 18 ? ' · more than 18 — multiple teams will share holes' : ''}
          </p>
        </div>
        {teams.length === 0 ? (
          <p className="text-base text-gray-500 italic">No teams yet — add teams from the Flights & Teams tab.</p>
        ) : (
          <table className="w-full text-base">
            <thead className="text-sm uppercase text-gray-500 border-b">
              <tr>
                <th className="text-left py-2 w-10">#</th>
                <th className="text-left py-2">Team Name</th>
                <th className="text-right py-2 w-32">Starting Hole</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((t, idx) => (
                <tr key={`${t.flightId}-${t.teamId}`} className={`border-b last:border-0 ${idx % 2 ? 'bg-gray-50' : ''}`}>
                  <td className="py-2 font-bold text-gray-500">{idx + 1}</td>
                  <td className="py-2 font-semibold">
                    {lastNames(t.info?.name) || '(unnamed)'}
                    {t.info?.players?.length ? (
                      <span className="text-sm text-gray-500 ml-2">{(t.info.players || []).join(' · ')}</span>
                    ) : null}
                  </td>
                  <td className="py-2 text-right">
                    <select
                      className="rounded-md border border-gray-300 px-2 py-1 text-base bg-white"
                      value={t.info?.startingHole || ''}
                      onChange={(e) => {
                        const v = Number(e.target.value)
                        const hole = Number.isFinite(v) && v >= 1 && v <= 18 ? v : null
                        setTeamStartingHole(tournamentId, t.flightId, t.teamId, hole).catch((err) =>
                          console.warn('[scramble shotgun] hole save failed', err))
                      }}>
                      <option value="">— pick —</option>
                      {Array.from({ length: 18 }, (_, i) => i + 1).map((h) => (
                        <option key={h} value={h}>Hole {h}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ── Shotgun panel: two wave cards + per-wave assignments table ────────────

function ShotgunDay({ tournamentId, day, flights, tournamentInfo, dayData, tournament, brackets }) {
  const flightIds = flights.map((f) => f.id)
  const wave1 = dayData?.waves?.wave1 || {}
  const wave2 = dayData?.waves?.wave2 || {}
  const isDay1 = Number(day) === 1

  const applyPbcc = async () => {
    const { wave1: w1Flights, wave2: w2Flights } = pbccRotationFor(day, flightIds)
    await Promise.all([
      setWaveStartTime(tournamentId, day, 'wave1', wave1.startTime || '8:00 AM'),
      setWaveStartTime(tournamentId, day, 'wave2', wave2.startTime || '1:00 PM'),
      setWaveFlights(tournamentId, day, 'wave1', w1Flights),
      setWaveFlights(tournamentId, day, 'wave2', w2Flights),
    ])
  }

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap items-center justify-between gap-2">
        <p className="text-base">
          {isDay1 ? (
            <>
              <b>Day 1</b> — admin can apply the flip rotation in one click,
              or check flights into each wave manually.
            </>
          ) : (
            <>
              <b>Day {day}</b> — auto-generate each wave from the previous day's
              wave results. Matchups, flights, and starting holes are computed
              from the bracket. Flip rotation applies: yesterday's AM teams play
              PM today, and vice versa.
            </>
          )}
        </p>
        {isDay1 ? (
          <button className="btn-secondary" onClick={applyPbcc}>
            ⟲ Apply Flip Rotation
          </button>
        ) : null}
      </div>

      <WaveCard
        tournamentId={tournamentId}
        day={day}
        waveKey="wave1"
        title="Wave 1"
        defaultTime="8:00 AM"
        flights={flights}
        tournamentInfo={tournamentInfo}
        wave={wave1}
        tournament={tournament}
        brackets={brackets}
      />

      <WaveCard
        tournamentId={tournamentId}
        day={day}
        waveKey="wave2"
        title="Wave 2"
        defaultTime="1:00 PM"
        flights={flights}
        tournamentInfo={tournamentInfo}
        wave={wave2}
        tournament={tournament}
        brackets={brackets}
      />
    </div>
  )
}

function WaveCard({ tournamentId, day, waveKey, title, defaultTime, flights, tournamentInfo, wave, tournament, brackets }) {
  const flightsInWave = wave?.flights || {}
  const startTime = wave?.startTime || ''
  const assignments = wave?.assignments || {}
  const isDay1 = Number(day) === 1
  // PBCC flip: each wave's source is the OPPOSITE wave on the previous day.
  const sourceDay = Number(day) - 1
  const sourceWaveKey = waveKey === 'wave1' ? 'wave2' : 'wave1'

  const [timeDraft, setTimeDraft] = useState(startTime || defaultTime)
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState(null)

  useEffect(() => {
    setTimeDraft(startTime || defaultTime)
  }, [startTime, defaultTime])

  const checkedFlightIds = flights
    .filter((f) => flightsInWave[f.id])
    .map((f) => f.id)

  // Day 1 → opponentId-based grouping (matches Day 1 bracket Round 1).
  // Day 2/3 → bracket-based grouping (rounds with day === selected day).
  const flightsById = useMemo(
    () => Object.fromEntries(flights.map((f) => [f.id, f])),
    [flights],
  )
  const groups = useMemo(() => {
    if (isDay1) {
      return buildWaveGroups(checkedFlightIds, flights, tournamentInfo)
    }
    return buildBracketWaveGroups(checkedFlightIds, flightsById, brackets || {}, day)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedFlightIds.join(','), flights, tournamentInfo?.format, brackets, day, isDay1])

  // Readiness check for the source wave (Day N-1, opposite waveKey).
  const readiness = useMemo(() => {
    if (isDay1 || !tournament) return null
    return waveAdvanceReadiness(tournament, sourceDay, sourceWaveKey)
  }, [isDay1, tournament, sourceDay, sourceWaveKey])

  const toggleFlight = async (fid, checked) => {
    const next = new Set(checkedFlightIds)
    if (checked) next.add(fid)
    else next.delete(fid)
    await setWaveFlights(tournamentId, day, waveKey, Array.from(next))
  }

  const autoAssign = async () => {
    const map = assignWaveHoles(groups)
    await setWaveAssignments(tournamentId, day, waveKey, map)
  }

  const clearAssignments = async () => {
    if (!confirm(`Clear all ${title} hole assignments for Day ${day}?`)) return
    await setWaveAssignments(tournamentId, day, waveKey, {})
  }

  const autoGenerate = async () => {
    setBusy(true)
    setFlash(null)
    try {
      const res = await autoGenerateNextDayFromWave(tournamentId, sourceDay, sourceWaveKey)
      if (res.ok) {
        setFlash({ kind: 'ok', msg: `✓ Generated ${res.groupsCount} match${res.groupsCount === 1 ? '' : 'es'} from Day ${sourceDay} ${sourceWaveKey === 'wave1' ? 'Wave 1' : 'Wave 2'} results.` })
      } else {
        setFlash({ kind: 'err', msg: res.message || 'Could not auto-generate.' })
      }
    } finally {
      setBusy(false)
      setTimeout(() => setFlash(null), 6000)
    }
  }

  return (
    <div className="card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="text-lg font-extrabold">{title}</h3>
        <div className="flex items-center gap-2">
          <label className="text-sm uppercase tracking-widest font-bold text-gray-600">Start Time</label>
          <input
            type="text"
            className="input w-28 text-base"
            placeholder={defaultTime}
            value={timeDraft}
            onChange={(e) => {
              const v = e.target.value
              setTimeDraft(v)
              // Persist on every keystroke. If admin closes the tab or
              // browser before the field blurs, the typed value is still
              // saved to Firebase. onBlur is a belt-and-suspenders trim.
              setWaveStartTime(tournamentId, day, waveKey, v)
            }}
            onBlur={() => setWaveStartTime(tournamentId, day, waveKey, timeDraft.trim())}
          />
        </div>
      </div>

      {!isDay1 ? (
        <AutoGenerateBanner
          sourceDay={sourceDay}
          sourceWaveKey={sourceWaveKey}
          readiness={readiness}
          busy={busy}
          flash={flash}
          onClick={autoGenerate}
        />
      ) : null}

      {isDay1 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 mb-3">
          {flights.map((f) => (
            <label key={f.id} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!flightsInWave[f.id]}
                onChange={(e) => toggleFlight(f.id, e.target.checked)}
              />
              <span className="text-base font-bold">{f.info?.name || f.id}</span>
            </label>
          ))}
        </div>
      ) : null}

      <div className="flex gap-2 mb-3">
        <button
          className="btn-secondary text-base"
          disabled={groups.length === 0}
          onClick={autoAssign}>
          ⛳ Auto-Assign Holes ({groups.length} group{groups.length === 1 ? '' : 's'})
        </button>
        {Object.keys(assignments).length > 0 ? (
          <button className="btn-ghost text-sm text-red-600 font-bold" onClick={clearAssignments}>
            Clear assignments
          </button>
        ) : null}
      </div>

      {groups.length === 0 ? (
        <p className="text-base text-gray-500 italic">
          {isDay1
            ? 'Check at least one flight into this wave to populate the group table.'
            : `Click Auto-Generate to populate Day ${day} ${title} from Day ${sourceDay} ${sourceWaveKey === 'wave1' ? 'Wave 1' : 'Wave 2'} results.`}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead className="text-sm uppercase text-gray-500 border-b">
              <tr>
                <th className="text-left py-2">Flight</th>
                <th className="text-left py-2">Match</th>
                <th className="text-left py-2">Teams</th>
                <th className="text-right py-2 w-32">Starting Hole</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const current = assignments[g.teamIds[0]] || ''
                return (
                  <GroupRow
                    key={g.key}
                    group={g}
                    current={current}
                    onSave={(value) =>
                      setGroupAssignment(tournamentId, day, waveKey, g.teamIds, value)
                    }
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function AutoGenerateBanner({ sourceDay, sourceWaveKey, readiness, busy, flash, onClick }) {
  const sourceLabel = sourceWaveKey === 'wave1' ? 'Wave 1' : 'Wave 2'
  const ready = readiness?.ready
  const noFlights = !readiness || readiness.totalMatches === 0

  let body
  if (noFlights) {
    body = (
      <p className="text-base text-gray-700">
        ⏳ Day {sourceDay} {sourceLabel} hasn't been set up yet. Configure that wave first.
      </p>
    )
  } else if (!ready) {
    body = (
      <p className="text-base text-gray-700">
        ⏳ Waiting for Day {sourceDay} {sourceLabel} results — Auto-Generate will activate when all matches are complete.
        {' '}
        <span className="text-sm text-gray-500">
          ({readiness.undeterminedMatches} of {readiness.totalMatches} match{readiness.totalMatches === 1 ? '' : 'es'} still in play)
        </span>
      </p>
    )
  } else {
    body = (
      <p className="text-base text-gray-700">
        ✅ All Day {sourceDay} {sourceLabel} matches complete — ready to generate.
      </p>
    )
  }

  return (
    <div className={`rounded-xl p-3 mb-3 border ${ready ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {body}
        <button
          className="btn-primary text-base"
          disabled={busy || !ready}
          onClick={onClick}>
          {busy ? 'Generating…' : `⚡ Auto-Generate from Day ${sourceDay} ${sourceLabel} Results`}
        </button>
      </div>
      {flash ? (
        <p className={`text-sm font-bold mt-2 ${flash.kind === 'ok' ? 'text-green-deep' : 'text-red-600'}`}>
          {flash.msg}
        </p>
      ) : null}
    </div>
  )
}

function GroupRow({ group, current, onSave }) {
  const [draft, setDraft] = useState(current)
  useEffect(() => { setDraft(current) }, [current])
  return (
    <tr className="border-b last:border-0">
      <td className="py-2 font-semibold whitespace-nowrap">{group.flightName}</td>
      <td className="py-2 whitespace-nowrap">{group.matchLabel}</td>
      <td className="py-2 text-gray-700">{String(group.label || '').split(' vs ').map(lastNames).join(' vs ')}</td>
      <td className="py-2 text-right">
        <input
          type="text"
          className="input py-1.5 text-base w-24 text-right font-mono"
          placeholder="—"
          value={draft}
          onChange={(e) => {
            const v = e.target.value
            setDraft(v)
            // Persist immediately. Closing the tab/browser before blur
            // still leaves the typed value in Firebase.
            onSave(v)
          }}
          onBlur={() => {
            const trimmed = draft.trim()
            if (trimmed !== current) onSave(trimmed)
          }}
        />
      </td>
    </tr>
  )
}

// ── Sequential panel ──────────────────────────────────────────────────────

function SequentialDay({ tournamentId, day, flights, tournamentInfo, dayData }) {
  const wave = dayData?.waves?.wave1 || {}
  const startTime = wave?.startTime || ''
  const [timeDraft, setTimeDraft] = useState(startTime || '08:00')
  const [interval, setInterval] = useState(10)

  useEffect(() => { setTimeDraft(startTime || '08:00') }, [startTime])

  // Sequential mode includes every flight automatically.
  const flightIds = flights.map((f) => f.id)
  const groups = useMemo(
    () => buildWaveGroups(flightIds, flights, tournamentInfo),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flightIds.join(','), flights, tournamentInfo?.format],
  )
  const assignments = wave?.assignments || {}

  const generate = async () => {
    await setWaveFlights(tournamentId, day, 'wave1', flightIds)
    await setWaveStartTime(tournamentId, day, 'wave1', timeDraft.trim())
    const map = assignWaveSequentialTimes(groups, timeDraft, interval)
    await setWaveAssignments(tournamentId, day, 'wave1', map)
  }

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Start Time (24h)</label>
          <input
            type="time"
            className="input w-32"
            value={timeDraft}
            onChange={(e) => setTimeDraft(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Interval (min)</label>
          <input
            type="number" min="1" max="60"
            className="input w-24"
            value={interval}
            onChange={(e) => setInterval(Number(e.target.value) || 10)}
          />
        </div>
        <button className="btn-primary" onClick={generate}>
          ⛳ Generate Day {day} Tee Times
        </button>
      </div>

      <div className="card">
        <h3 className="text-lg font-extrabold mb-3">
          Day {day} Tee Times ({groups.length} group{groups.length === 1 ? '' : 's'})
        </h3>
        {groups.length === 0 ? (
          <p className="text-base text-gray-500 italic">No groups yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-base">
              <thead className="text-sm uppercase text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2">Flight</th>
                  <th className="text-left py-2">Match</th>
                  <th className="text-left py-2">Teams</th>
                  <th className="text-right py-2 w-32">Tee Time</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const current = assignments[g.teamIds[0]] || ''
                  return (
                    <GroupRow
                      key={g.key}
                      group={g}
                      current={current}
                      onSave={(value) =>
                        setGroupAssignment(tournamentId, day, 'wave1', g.teamIds, value)
                      }
                    />
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
